import { DeviceResponse } from '../types/api/types';
import { getApi } from '.';
import {
  getAccountStore,
  getRulesStore,
  getReportedDeviceInfoStore,
} from '../storage/stores';

import {
  CHROMEBOOK_OS_DEVICE_PLATFORM,
  CHROMEBOOK_OS_DEVICE_TYPE,
  CHROMEBOOK_OS_DEVICE_UNAME,
  CHROMEBOOK_OS_DEVICE_UUID,
} from '../constants';
import { getExtensionVersion } from '../chrome/api';
import { OnBoardingError } from '../lib/errors/OnBoardingError';
import { APIError } from '../lib/errors/api/APIError';

type DeviceUid = DeviceResponse['data']['uid'];

export const fetchDeviceProfileData = async (
  deviceUid: string
): Promise<{ profileId: number; profileName: string }> => {
  const { data } = await getApi().device.get<DeviceResponse>({
    deviceUid,
  });
  if (!data.users) {
    throw new Error('Invalid device data!');
  }

  return {
    profileId: data.users[0].profile_id,
    profileName: data.users[0].name,
  };
};

/**
 * when an API call fails, this function will return an OnBoardingError if the
 * error is an APIError or an Error.
 */
const apiCallErrorManager = <T>(
  error: T,
  defaultMessage = ''
): OnBoardingError | T => {
  if (error instanceof APIError) {
    return new OnBoardingError(
      defaultMessage === ''
        ? error.message
        : `${defaultMessage}: ${error.message}`,
      error.status
    );
  }
  if (error instanceof Error) {
    return new OnBoardingError(
      defaultMessage === ''
        ? error.message
        : `${defaultMessage}: ${error.message}`
    );
  }
  return error;
};

export type DeviceType = 'delegated_chromebook' | 'android_test';

/**
 * Creates a new device and assigns it to the provided profile.
 *
 * Use deviceType "android_test" to create an Android device for testing, or "delegated_chromebook" for a School Chromebook device.
 *
 * For Android devices, some details are hardcoded! Use only for testing purposes.
 */
export const createDevice = async (
  deviceName: string,
  profileId: number,
  deviceType: DeviceType
) => {
  switch (deviceType) {
    case 'android_test':
      return createTestAndroidDevice(deviceName, profileId);
    case 'delegated_chromebook':
      return createDelegatedChromebookDevice(deviceName, profileId);
  }
};

/**
 * Creates an Android device for testing.
 *
 * Do NOT use it for creating production devices!
 */
const createTestAndroidDevice = async (
  deviceName: string,
  profileId: number
) => {
  const timezone = getRulesStore().getTimezone();
  const body = {
    name: deviceName,
    timezone: timezone,
    version: '180.67.0.2-family',
    type: 'LAPTOP',
    platform: 2,
    users: [
      {
        name: CHROMEBOOK_OS_DEVICE_UNAME,
        uid: CHROMEBOOK_OS_DEVICE_UUID,
        profile_id: profileId,
      },
    ],
  };

  return postDevice(body);
};

/**
 * Creates a delegated device in this account
 */
const createDelegatedChromebookDevice = async (
  deviceName: string,
  profileId: number
) => {
  const accountInfo = getAccountStore().get();
  const timezone = getRulesStore().getTimezone();
  const extensionVersion = getExtensionVersion();
  const body = {
    enabled: 1,
    name: deviceName,
    timezone: timezone,
    version: extensionVersion,
    account_id: accountInfo?.accountId,
    type: CHROMEBOOK_OS_DEVICE_TYPE,
    platform: CHROMEBOOK_OS_DEVICE_PLATFORM,
    is_delegated: true,
    users: [
      {
        name: CHROMEBOOK_OS_DEVICE_UNAME,
        uid: CHROMEBOOK_OS_DEVICE_UUID,
        profile_id: profileId,
      },
    ],
  };

  const postResponse = postDevice(body);

  // Set current extension version as reported
  await getReportedDeviceInfoStore().set({
    extensionVersion,
  });

  return postResponse;
};

const postDevice = async (body: object) => {
  try {
    const { data } = await getApi().devices.post<DeviceResponse>(
      body,
      null,
      {}
    );
    return data;
  } catch (error) {
    throw apiCallErrorManager(error, 'Error creating device');
  }
};

/**
 * Replace existing device
 */
export const replaceDevice = async (
  deviceName: string,
  profileId: number,
  deviceType: DeviceType
): Promise<string> => {
  try {
    const deviceUid = await getAccountDeviceUID(deviceName, profileId);
    await deleteDevice(deviceUid);
    const device = await createDevice(deviceName, profileId, deviceType);
    return device.uid;
  } catch (error) {
    throw apiCallErrorManager(error, 'Error replacing device');
  }
};

/**
 * Returns the profile id the device is currently assigned to in the backend, or
 * `undefined` for definitive not-found responses (404 / 410).
 *
 * Transient failures (5xx, network) are rethrown so callers do not recreate a
 * device on a recoverable error, which would defeat the duplicate-device
 * protection. Callers use the returned profile to decide whether a persisted
 * device can be safely reused for the current profile.
 */
export const getDeviceProfileId = async (
  deviceUid: string
): Promise<number | undefined> => {
  try {
    const { profileId } = await fetchDeviceProfileData(deviceUid);
    return profileId;
  } catch (error) {
    if (
      error instanceof APIError &&
      (error.status === APIError.STATUS_NOT_FOUND ||
        error.status === APIError.STATUS_GONE)
    ) {
      return undefined;
    }
    throw error;
  }
};

/**
 * get the deviceUID for a device
 */
export const getAccountDeviceUID = async (
  deviceName: string,
  profileId: number
) => {
  const { data } = await getApi().devices.get<{
    data: DeviceResponse['data'][];
  }>({});
  const filteredDevice = data.filter(device => device.name === deviceName);
  if (filteredDevice.length > 0) {
    if (!filteredDevice[0].users[0].profile_id) {
      throw new OnBoardingError(
        `No profile assigned to this device: ${deviceName}`,
        400
      );
    }
    if (filteredDevice[0].users[0].profile_id !== profileId) {
      throw new OnBoardingError(
        `the device ${deviceName} is assigned not assigned to the profile: ${profileId}.`,
        400
      );
    }
    return filteredDevice[0].uid;
  } else {
    throw new OnBoardingError(
      `Trying to get non existing device: ${deviceName}`,
      400
    );
  }
};

export const deleteDevice = (deviceUid: DeviceUid) =>
  getApi().device.delete({ deviceUid });

export const updateDeviceExtensionVersion = async (
  deviceUID: DeviceUid,
  updatedVersion: string
) => {
  const body = {
    version: updatedVersion,
  };
  await getApi().deviceVersion.post(body, null, { deviceUID });
};

export const postDeviceDetails = async (
  deviceUID: DeviceUid,
  osVersion: string
) => {
  const body = {
    os_version: osVersion,
    model: '', // Empty, because can't get the model name from Chromebooks, and the field is required.
  };
  return getApi().deviceDetails.post(body, null, { deviceUID });
};

export const postDeviceOptions = async (
  deviceUid: DeviceUid,
  userUid: string,
  key: string,
  value: string
) => {
  const body = {
    [key]: value,
  };
  return getApi().deviceOptions.post(body, null, { deviceUid, userUid });
};
